import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ArrowRight } from 'lucide-react';
import { wistiaEmbed } from '../ThreeJobs';
import { mergeAll } from './content';

const API = process.env.REACT_APP_BACKEND_URL;

// EditCtx is null on live pages; in the editor preview it carries {selected, onSelect, onInline, drag, onDragStart, onDragEnd, onDropExtra}
export const EditCtx = createContext(null);
// PageCtx carries {config, handlers, jobs} to blocks
export const PageCtx = createContext({ config: {}, handlers: {}, jobs: null });

// Size multipliers relative to each element's own designed size ('' / 'lg' = default)
export const SIZE_EM = { xs: '0.65em', sm: '0.8em', md: '0.9em', lg: '1em', xl: '1.25em', xxl: '1.5em' };
export const WIDTH_REM = { narrow: '32rem', medium: '42rem', wide: '60rem' };

/* ── deep path helpers (dotted paths, array indexes ok) ── */
export const getDeep = (obj, path) => path.split('.').reduce((a, kk) => (a == null ? a : a[kk]), obj);
export const setDeep = (obj, path, val) => {
  const keys = path.split('.');
  const root = Array.isArray(obj) ? [...obj] : { ...obj };
  let cur = root;
  for (let i = 0; i < keys.length - 1; i++) {
    const kk = keys[i];
    const nxt = cur[kk];
    const copy = Array.isArray(nxt) ? [...nxt] : { ...(nxt || {}) };
    cur[kk] = copy;
    cur = copy;
  }
  cur[keys[keys.length - 1]] = val;
  return root;
};

/* ── markup engine ──────────────────────────────────────────────────────
   Grammar: **bold**  __underline__  *serif accent*  {color:#hex}words{/color}
            newlines, {{date}}, {{time}} — all nestable in any order        */
const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const INLINE_RE = /(\*\*)([\s\S]+?)\*\*|(__)([\s\S]+?)__|\{color:([^}]+)\}([\s\S]*?)\{\/color\}|(\*)([^*\n]+)\*|(\n)/;

const inlineHtml = (s, o) => {
  let out = '';
  let rest = s;
  while (rest) {
    const m = rest.match(INLINE_RE);
    if (!m) { out += esc(rest); break; }
    out += esc(rest.slice(0, m.index));
    if (m[1]) out += `<strong class="${o.strongClass}"${o.inColor ? ' style="color:inherit"' : ''}>${inlineHtml(m[2], o)}</strong>`;
    else if (m[3]) out += `<u>${inlineHtml(m[4], o)}</u>`;
    else if (m[5]) out += `<span style="color:${esc(m[5])}">${inlineHtml(m[6], { ...o, inColor: true })}</span>`;
    else if (m[7]) out += `<em class="arc-serif font-medium">${inlineHtml(m[8], o)}</em>`;
    else out += '<br>';
    rest = rest.slice(m.index + m[0].length);
  }
  return out;
};

export const markupToHtml = (text, o = {}) => {
  if (text === undefined || text === null || text === '') return '';
  const cfg = o.config || {};
  const opts = { strongClass: o.strongClass || 'font-bold' };
  const s = String(text).replaceAll('{{date}}', '\u0001date\u0001').replaceAll('{{time}}', '\u0001time\u0001');
  let html = inlineHtml(s, opts);
  const tok = (name, val) => (o.editing
    ? `<span data-token="${name}" contenteditable="false">${esc(val)}</span>`
    : esc(val));
  html = html
    .replaceAll('\u0001date\u0001', tok('date', cfg.event_date || '[DATE]'))
    .replaceAll('\u0001time\u0001', tok('time', cfg.event_time || '[TIME]'));
  return html;
};

/* DOM (contentEditable) → markup */
const rgbToHex = (c) => {
  const m = String(c).match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!m) return c;
  return '#' + [1, 2, 3].map((i) => (+m[i]).toString(16).padStart(2, '0')).join('');
};

export const htmlToMarkup = (root) => {
  const walk = (node) => {
    let out = '';
    node.childNodes.forEach((n) => {
      if (n.nodeType === 3) { out += n.textContent.replace(/\u00a0/g, ' '); return; }
      if (n.nodeType !== 1) return;
      if (n.nodeName === 'BR') { out += '\n'; return; }
      if (n.dataset && n.dataset.token) { out += `{{${n.dataset.token}}}`; return; }
      let inner = walk(n);
      const st = n.style || {};
      const isB = n.nodeName === 'B' || n.nodeName === 'STRONG' || parseInt(st.fontWeight, 10) >= 600;
      const isI = n.nodeName === 'I' || n.nodeName === 'EM' || (n.classList && n.classList.contains('arc-serif')) || st.fontStyle === 'italic';
      const isU = n.nodeName === 'U' || String(st.textDecoration || '').includes('underline') || String(st.textDecorationLine || '').includes('underline');
      let color = st.color || (n.nodeName === 'FONT' && n.getAttribute('color')) || '';
      if (color === 'inherit' || color === 'currentcolor' || color === 'currentColor') color = '';
      if (inner.trim()) {
        if (isB) inner = `**${inner}**`;
        if (isI) inner = `*${inner}*`;
        if (isU) inner = `__${inner}__`;
        if (color) inner = `{color:${rgbToHex(color)}}${inner}{/color}`;
      }
      if ((n.nodeName === 'DIV' || n.nodeName === 'P') && out && !out.endsWith('\n')) out += '\n';
      out += inner;
    });
    return out;
  };
  return walk(root);
};

export const renderRich = (text, config, strongClass = 'font-bold') => {
  const html = markupToHtml(text, { config, strongClass });
  return html ? <span dangerouslySetInnerHTML={{ __html: html }} /> : null;
};

export const RichSpan = ({ v, strongClass }) => {
  const { config } = useContext(PageCtx);
  return <>{renderRich(v && v.t, config, strongClass)}</>;
};

/* Wraps a non-text element (video, composite) to make it click-to-select */
export const Editable = ({ sec, k, children }) => {
  const edit = useContext(EditCtx);
  if (!edit || !React.isValidElement(children)) return children;
  const id = `${sec}:${k ?? ''}`;
  const selected = edit.selected === id;
  return React.cloneElement(children, {
    onClickCapture: (e) => {
      if (!selected) { e.preventDefault(); e.stopPropagation(); edit.onSelect(sec, k); }
      else e.stopPropagation();
    },
    className: `${children.props.className || ''} vx-editable ${selected ? 'vx-selected' : ''}`,
  });
};

/* Inline-editable rich text host (only mounted in edit mode) */
const InlineText = ({ Tag, base, outer, inner, sec, k, t, config, strongClass, testid, edit }) => {
  const ref = useRef(null);
  const focused = useRef(false);
  const id = `${sec}:${k}`;
  const selected = edit.selected === id;
  useEffect(() => {
    if (ref.current && !focused.current) {
      ref.current.innerHTML = markupToHtml(t, { config, strongClass, editing: true });
    }
  });
  useEffect(() => {
    if (selected && ref.current && !focused.current) {
      setTimeout(() => {
        if (!ref.current) return;
        ref.current.focus();
        try {
          const r = document.createRange();
          r.selectNodeContents(ref.current);
          r.collapse(false);
          const s = window.getSelection();
          s.removeAllRanges();
          s.addRange(r);
        } catch { /* noop */ }
      }, 30);
    }
  }, [selected]);
  const commit = () => { if (ref.current && edit.onInline) edit.onInline(sec, k, htmlToMarkup(ref.current)); };
  return (
    <Tag
      className={`${base} vx-editable ${selected ? 'vx-selected' : ''}`}
      style={selected ? { ...outer, position: 'relative' } : outer}
      data-testid={testid}
      onClickCapture={(e) => {
        if (!selected) { e.preventDefault(); e.stopPropagation(); edit.onSelect(sec, k); }
        else e.stopPropagation();
      }}
    >
      <span
        ref={ref}
        style={{ ...inner, outline: 'none', minWidth: '1ch', display: 'inline-block' }}
        contentEditable="true"
        suppressContentEditableWarning
        spellCheck={false}
        data-vx-inline={`${sec}|${k}`}
        onFocus={() => { focused.current = true; }}
        onBlur={() => { if (window.__vxTbLock) return; focused.current = false; commit(); }}
        onInput={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); document.execCommand('insertLineBreak'); }
          if (e.key === 'Escape') e.currentTarget.blur();
        }}
      />
      {selected && edit.onHide && (!String(k).includes('.') || /^extras\.\d+\.v$/.test(String(k))) && (
        <button
          className="vx-del-chip"
          contentEditable={false}
          title="Remove this element — bring it back with the eye icon in the left panel"
          data-testid={`vx-del-${sec}-${k}`}
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); edit.onHide(sec, k); }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >✕ Remove</button>
      )}
    </Tag>
  );
};

/* Styled, editable text element. v = {t, size, bold, underline, color, width} or a plain string */
export const Tx = ({ v, sec, k, as = 'p', base = '', strongClass = 'font-bold', style, testid }) => {
  const edit = useContext(EditCtx);
  const { config } = useContext(PageCtx);
  const f = typeof v === 'string' ? { t: v } : (v || {});
  if (f.hidden) return null;
  const outer = { ...(style || {}) };
  if (f.width && WIDTH_REM[f.width]) { outer.maxWidth = WIDTH_REM[f.width]; outer.marginLeft = 'auto'; outer.marginRight = 'auto'; }
  if (f.width === 'full') outer.maxWidth = 'none';
  const inner = {};
  if (f.size && SIZE_EM[f.size]) inner.fontSize = SIZE_EM[f.size];
  if (f.bold) inner.fontWeight = 800;
  if (f.underline) inner.textDecoration = 'underline';
  if (f.color) inner.color = f.color;
  const Tag = as;
  if (!edit) {
    const html = markupToHtml(f.t, { config, strongClass });
    if (!html) return null;
    return (
      <Tag className={base} style={outer} data-testid={testid}>
        <span style={inner} dangerouslySetInnerHTML={{ __html: html }} />
      </Tag>
    );
  }
  return <InlineText Tag={Tag} base={base} outer={outer} inner={inner} sec={sec} k={k} t={f.t} config={config} strongClass={strongClass} testid={testid} edit={edit} />;
};

/* Inline-editable button label */
const CtaLabel = ({ sec, k, label, config, selected, edit }) => {
  const ref = useRef(null);
  const focused = useRef(false);
  useEffect(() => {
    if (ref.current && !focused.current) {
      ref.current.innerHTML = markupToHtml(label, { config, strongClass: 'font-black', editing: true });
    }
  });
  const commit = () => { if (ref.current && edit.onInline) edit.onInline(sec, `${k}.label`, htmlToMarkup(ref.current)); };
  return (
    <span
      ref={ref}
      style={{ outline: 'none', minWidth: '1ch', textAlign: 'center' }}
      contentEditable="true"
      suppressContentEditableWarning
      spellCheck={false}
      data-vx-inline={`${sec}|${k}.label`}
      onFocus={() => { focused.current = true; }}
      onBlur={() => { if (window.__vxTbLock) return; focused.current = false; commit(); }}
      onInput={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.execCommand('insertLineBreak'); } }}
    />
  );
};

/* Configurable button click action: url / funnel page / scroll-to-section.
   Returns 'default' when the caller should run its original behavior. */
export const ctaGo = (c = {}) => {
  if (c.action === 'url' && c.href) {
    if (c.new_tab) window.open(c.href, '_blank', 'noopener');
    else window.location.href = c.href;
    return 'done';
  }
  if (c.action === 'page' && c.page) { window.location.href = c.page; return 'done'; }
  if (c.action === 'section' && c.to_sec) {
    const el = document.querySelector(`[data-vx-sec="${c.to_sec}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return 'done';
  }
  return 'default';
};

/* Editable CTA button — supports multiline labels, markup, attention fx and click actions */
export const Cta = ({ cta, sec, k = 'cta', action = 'quiz', testid, className = '', arrow = true }) => {
  const { handlers, config } = useContext(PageCtx);
  const edit = useContext(EditCtx);
  const c = cta || {};
  if (c.hidden) return null;
  const fx = c.fx || 'arrow';
  const baseClick = action === 'quiz' ? handlers.openQuiz : typeof action === 'function' ? action : undefined;
  const onClick = () => { if (ctaGo(c) === 'default' && baseClick) baseClick(); };
  const cls = `arc-body group inline-flex items-center justify-center gap-2 rounded-full text-base md:text-lg font-bold px-9 py-4 transition-opacity hover:opacity-90 shadow-[0_8px_30px_rgba(20,20,20,0.2)] ${fx === 'nudge' ? 'vx-fx-nudge' : ''} ${fx === 'pulse' ? 'vx-fx-pulse' : ''} ${className}`;
  const styleBtn = { background: c.bg || '#141414', color: c.fg || '#F0EFEC' };
  const label = c.label || 'Save my free seat';
  if (!edit) {
    return (
      <button onClick={onClick} style={styleBtn} className={cls} data-testid={testid}>
        <span dangerouslySetInnerHTML={{ __html: markupToHtml(label, { config, strongClass: 'font-black' }) }} />
        {arrow && <ArrowRight size={18} className="transition-transform group-hover:translate-x-1 shrink-0" />}
      </button>
    );
  }
  const id = `${sec}:${k}`;
  const selected = edit.selected === id;
  return (
    <div
      role="button"
      style={styleBtn}
      className={`${cls} relative cursor-pointer vx-editable ${selected ? 'vx-selected' : ''}`}
      data-testid={testid}
      onClickCapture={(e) => {
        if (!selected) { e.preventDefault(); e.stopPropagation(); edit.onSelect(sec, k); }
        else e.stopPropagation();
      }}
    >
      <CtaLabel sec={sec} k={k} label={label} config={config} selected={selected} edit={edit} />
      {arrow && <ArrowRight size={18} className="shrink-0" />}
      {selected && edit.onHide && (!String(k).includes('.') || /^extras\.\d+\.v$/.test(String(k))) && (
        <button
          className="vx-del-chip"
          contentEditable={false}
          title="Remove this button — bring it back with the eye icon in the left panel"
          data-testid={`vx-del-${sec}-${k}`}
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); edit.onHide(sec, k); }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >✕ Remove</button>
      )}
    </div>
  );
};

/* Section wrapper — selectable, scroll target, own background overrides page bg */
export const Sec = ({ id, as = 'section', className = '', style, bg, testid, children }) => {
  const edit = useContext(EditCtx);
  const Tag = as;
  const st = { ...(style || {}) };
  if (bg) st.background = bg;
  return (
    <Tag
      data-vx-sec={id}
      className={className}
      style={st}
      data-testid={testid}
      onClick={edit ? () => edit.onSelect(id, null) : undefined}
    >
      {children}
    </Tag>
  );
};

/* Editable video embed (Wistia) with a click-shield in edit mode */
export const VideoFrame = ({ url, sec, k, className = '', placeholder = null, testid }) => {
  const edit = useContext(EditCtx);
  const embed = wistiaEmbed(url || '');
  const inner = embed ? (
    <div className="aspect-video relative">
      <iframe src={embed} title="video" allow="autoplay; fullscreen" allowFullScreen className="w-full h-full" />
      {edit && <div className="absolute inset-0" />}
    </div>
  ) : placeholder;
  const el = <div className={className} data-testid={testid}>{inner}</div>;
  return <Editable sec={sec} k={k}>{el}</Editable>;
};

/* Drop target shown while an added element is being dragged */
const DropZone = ({ sec, at, beforeId, edit }) => {
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); edit.onDropExtra(sec, at, beforeId); }}
      className={`vx-dropzone ${over ? 'vx-dropzone-over' : ''}`}
      data-testid={`dropzone-${sec}-${at}${beforeId ? `-${beforeId}` : ''}`}
    />
  );
};

/* Extra elements (added text / button / video) anchored inside a section.
   In edit mode each gets a drag handle; while dragging, drop zones appear
   at every anchor of every section so elements can move anywhere. */
export const Ext = ({ sec, p, at = 'end' }) => {
  const edit = useContext(EditCtx);
  const extras = (p && p.extras) || [];
  const items = extras.map((x, i) => ({ x, i })).filter(({ x }) => (x.at || 'end') === at);
  const dragging = edit && edit.drag;
  if (!items.length && !dragging) return null;

  const renderItem = ({ x, i }) => {
    const key = `extras.${i}.v`;
    let el;
    if (x.type === 'button') {
      el = (
        <div className="my-6 text-center">
          <Cta cta={x.v} sec={sec} k={key} testid={`extra-${x.id}`} />
        </div>
      );
    } else if (x.type === 'video') {
      el = (
        <VideoFrame
          url={typeof x.v === 'string' ? x.v : ''}
          sec={sec}
          k={key}
          className="my-6 max-w-2xl mx-auto rounded-2xl overflow-hidden bg-[#141414] shadow-[0_18px_60px_rgba(20,20,20,0.2)]"
          testid={`extra-${x.id}`}
          placeholder={(
            <div className="aspect-video flex items-center justify-center">
              <span className="arc-body text-sm font-bold text-white/50">Add a Wistia link in the editor</span>
            </div>
          )}
        />
      );
    } else {
      el = (
        <Tx
          v={x.v}
          sec={sec}
          k={key}
          base="arc-body text-base sm:text-lg text-[#141414]/70 leading-relaxed my-5 text-center"
          strongClass="font-bold text-[#141414]"
          testid={`extra-${x.id}`}
        />
      );
    }
    if (!edit) return <React.Fragment key={x.id}>{el}</React.Fragment>;
    return (
      <div key={x.id} className="vx-ex-wrap">
        <span
          draggable
          contentEditable={false}
          onDragStart={(e) => {
            e.stopPropagation();
            e.dataTransfer.effectAllowed = 'move';
            try { e.dataTransfer.setData('text/plain', x.id); } catch { /* noop */ }
            setTimeout(() => edit.onDragStart(sec, x.id), 0);
          }}
          onDragEnd={() => edit.onDragEnd()}
          className="vx-drag-handle"
          title="Drag to move this element anywhere"
          data-testid={`extra-drag-${x.id}`}
        >⠿ drag</span>
        {el}
      </div>
    );
  };

  return (
    <>
      {items.map((it) => (
        <React.Fragment key={it.x.id}>
          {dragging && <DropZone sec={sec} at={at} beforeId={it.x.id} edit={edit} />}
          {renderItem(it)}
        </React.Fragment>
      ))}
      {dragging && <DropZone sec={sec} at={at} beforeId={null} edit={edit} />}
    </>
  );
};

/* Page-level background from the pinned page_settings section */
export const pageBg = (sections) => {
  const s = (sections || []).find((x) => x.type === 'page_settings');
  return (s && s.props && s.props.bg) || '';
};

/* Fetch saved page content (public endpoint) merged over defaults */
export const useFunnelContent = () => {
  const [pages, setPages] = useState(() => mergeAll({}));
  useEffect(() => {
    axios.get(`${API}/api/funnel/content`)
      .then((r) => setPages(mergeAll((r.data && r.data.pages) || {})))
      .catch(() => {});
  }, []);
  return pages;
};

export const sectionProps = (sections, type) => {
  const s = (sections || []).find((x) => x.type === type);
  return s ? s.props : null;
};
